/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/prop-types */
import currency from "currency.js";
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSortBy, useTable } from "react-table";
import { GetSettlementHistory200Response } from "../../../codegen-api";
import { ITableColumn } from "../../../interfaces/Table/TableColumn";
import { epochSecondsFrom, getTimeAgo, nanosToSeconds } from "../../../utils/date";
import { Spinner } from "../../shared/Spinner";
import { SpinnerContainerWrapper } from "../../shared/Spinner/style";
import { DefaultCellForColumn } from "../../shared/Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../../shared/Table/DefaultHeaderForColumn";
import MarketCell from "../Table/MarketCell";
import { EmptyContentContainer, PortfolioTableWrapper } from "../Table/style";
import { UNKNOWN_DISPLAY } from "../../../utils/unknownNumbers";

interface ITradeHistoryProps {
  settlementHistories?: GetSettlementHistory200Response[];
  loading?: boolean;
  isMobile?: boolean;
  height?: number;
}

function PortfolioSettlementHistory({
  settlementHistories,
  loading,
  isMobile,
}: ITradeHistoryProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "PortfolioSettings.PortfolioSettlementHistory",
  });
  const { t: translateAll } = useTranslation();
  const { t: tooltip } = useTranslation("tooltips");

  const modifiedSettlementHistory: GetSettlementHistory200Response[] = useMemo(
    () => settlementHistories || [],
    [settlementHistories]
  );

  const columns: ITableColumn<GetSettlementHistory200Response>[] = useMemo(
    () => [
      {
        title: t("asset"),
        accessor: "asset",
        tooltip: tooltip("asset"),
        Cell: ({ value }) => <MarketCell instrumentName={value} />,
      },
      {
        title: t("expiry"),
        accessor: "expiry",
        align: "left",
        tooltip: tooltip("expiry"),
        valueExtractor: (value) =>
          `${moment
            .unix(nanosToSeconds(value))
            .utc()
            .format("hhmm")} UTC - ${moment
            .unix(nanosToSeconds(value))
            .format("DD MMM YY")}`,
      },
      {
        title: t("settlement_price"),
        accessor: "settlement_price",
        align: "right",
        tooltip: tooltip("settlementPrice"),
        valueExtractor: (value) => currency(value).format(),
      },
      {
        title: t("settlement_time"),
        accessor: "settlement_timestamp",
        align: "right",
        tooltip: tooltip("settlementTime"),
        valueExtractor: (value) =>
          (() => {
          // The API sends this in NANOseconds. Read as seconds it lands
          // ~56bn years ahead, and getTimeAgo calls any future date
          // "just now" -- so every row read "Just Now", four-month-old
          // orders included.
          const seconds = epochSecondsFrom(value as string | number);
          return seconds === undefined
            ? UNKNOWN_DISPLAY
            : getTimeAgo(moment.unix(seconds), translateAll);
        })(),
      },
    ],
    [t, tooltip, translateAll]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: modifiedSettlementHistory,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        },
        autoResetSortBy: false,
      } as any,
      useSortBy
    );

  return (
    <PortfolioTableWrapper $stickyHeader isMobile={isMobile}>
      {loading || !modifiedSettlementHistory.length ? (
        <div>
          {loading ? (
            <SpinnerContainerWrapper>
              <Spinner />
            </SpinnerContainerWrapper>
          ) : (
            <EmptyContentContainer>{t("no_history")}</EmptyContentContainer>
          )}
        </div>
      ) : (
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...trProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...trProps}>
                  {headerGroup.headers.map((column: any) => {
                    const { key, ...thProps } = column.getHeaderProps(column.getSortByToggleProps());
                    return (
                      // <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                      <th key={key} {...thProps}>
                        {column.render("Header")}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const { key, ...rowProps } = row.getRowProps();
              return (
                <tr key={key} {...rowProps}>
                  {row.cells.map((cell) => {
                    const { key, ...tdProps } = cell.getCellProps();
                    return (
                      <td key={key} {...tdProps}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </PortfolioTableWrapper>
  );
}

export default PortfolioSettlementHistory;
